import { useState, useCallback, useMemo, useRef } from 'react';
// Sub-components
import ChordDiagram from './ChordDiagram';
import NoraMode from './NoraMode';
import Menu from './Menu';
import useWindowDimensions from './useWindowDimensions';
// Tools
import { count_covoyagers } from './utils';
// Constants
import { NORA_NAME } from './constants';

// ***************************************************************************
// * Ordering
// ***************************************************************************
// The three orderings, each as a sort key over a person: lower sorts earlier, so
// the "most X" ones are negated. The two counting orderings count only what the
// filter leaves in, which is what makes the displayed order follow the filter.
// First trip deliberately does not: it is when someone first voyaged, which is a
// fact about them, not about what is currently on screen.
const ORDERINGS = {
  first_trip: (person) => person.person_trips[0].trip_index,
  num_trips: (person) => -person.person_trips.filter((pt) => pt.in_filter).length,
  num_covoyagers: (person) => -count_covoyagers(person, true)
};

// The orderings offered by the "Order by:" buttons. This is also the priority
// order the three of them are applied in to produce the default ordering.
const ORDER_OPTIONS = [
  { key: "first_trip",     label: "first trip" },
  { key: "num_trips",      label: "number of trips" },
  { key: "num_covoyagers", label: "number of co-voyagers" }
];
const DEFAULT_ORDER_KEYS = ORDER_OPTIONS.map((option) => option.key);

// Orders people - given as a list of columns into people_by_column - by the named
// orderings, highest priority first. The sort is stable and applied in reverse
// priority order, so ties fall through to the lower priority orderings and
// finally to the order the columns came in.
const order_people = (columns, people_by_column, order_keys) => {
  let ordered_columns = [...columns];
  for (let i = order_keys.length - 1; i >= 0; i--) {
    const ordering = ORDERINGS[order_keys[i]];
    // Compute each person's sort key once rather than on every comparison.
    const keys = Object.fromEntries(columns.map((c) => [c, ordering(people_by_column[c])]));
    ordered_columns.sort((a, b) => keys[a] - keys[b]);
  }
  return ordered_columns;
};

// Owns everything that is true of the data regardless of how it is drawn: the
// people/trips graph, the order people are drawn in, and the selection/focus/
// filter state. It then renders one of the two views (chord diagram or Nora
// mode), with the header sitting above both.
//
// Note the angles on people/person_trips are computed here too even though only
// the chord diagram uses them. Keeping the graph and its geometry together is
// how this code grew up, and the cost is negligible at this data size.
const VoyageModel = ({ data_names, trip_dates, trip_key_memories, data_binary, hammock_diary_urls }) => {

  // ***************************************************************************
  // * Building the people/trips graph
  // ***************************************************************************
  // Builds the people and the trips out of the raw table, along with the
  // person_trips that join them, and marks each one with whether the filter lets
  // it through. Two things are deliberately left blank here: person_index, which
  // comes from the default ordering (which is itself computed from this graph),
  // and the angles, which depend on the order people are currently drawn in.
  //
  // People are keyed by their column in the raw table. Anyone who never voyaged
  // is left out of the model altogether.
  const build_graph = useCallback((column_in_filter, trip_index_in_filter) => {
    let people_by_column = {};
    let trips = {};
    for (let column = 0; column < data_names.length; column++) {
      const person_in_filter = column_in_filter(column);
      let person_trips = [];
      let group_size = null;
      for (let j = 0; j < data_binary.length; j++) {
        if (!data_binary[j][column]) continue;
        //
        // Create person_trip for this person on trip j
        //
        if (group_size !== null && data_binary[j][column] !== group_size) {
          console.log(`WARNING: Inconsistent group size under ${data_names[column]}`);
        }
        group_size = data_binary[j][column];
        const trip_in_filter = trip_index_in_filter(j);
        let person_trip = {
          person_index : null,
          in_filter : person_in_filter && trip_in_filter,
          trip_index : j,
          start_angle : null,
          end_angle : null
        };
        person_trips.push(person_trip);

        if (j in trips) {
          // Add this person_trip to existing trip
          trips[j].person_trips.push(person_trip);
          trips[j].trip_size += group_size;
        } else {
          // Create new trip object with this person_trip
          trips[j] = {
            trip_index : j,
            trip_date : trip_dates[j],
            trip_key_memory : trip_key_memories[j],
            in_filter : trip_in_filter,
            person_trips : [person_trip],
            trip_size : group_size
          };
        }

        // Add pointer to trip into person_trip
        person_trip.trip = trips[j];
      }

      // Only people who voyaged are in the model at all
      if (person_trips.length === 0) continue;
      let person = {
        person_index : null,
        name : data_names[column],
        in_filter : person_in_filter,
        start_angle : null,
        end_angle : null,
        person_trips : person_trips,
        group_size : group_size
      };
      // Add pointer to person into person_trip
      for (let pt in person_trips) {
        person_trips[pt].person = person;
      }
      people_by_column[column] = person;
    }
    return [people_by_column, trips];
  }, [data_binary, data_names, trip_dates, trip_key_memories]);

  // ***************************************************************************
  // * Default ordering
  // ***************************************************************************
  // person_index is the identity everything else hangs off - the filter, the
  // colours, the selection - so it has to stay put. It is a person's position in
  // this ordering, which is computed once from the unfiltered data by applying
  // all three orderings in priority order, and then never recomputed. Only the
  // order people are *drawn* in moves after this.
  const default_people_sorting = useMemo(() => {
    const [people_by_column] = build_graph(() => true, () => true);
    const columns = Object.keys(people_by_column).map(Number);
    return order_people(columns, people_by_column, DEFAULT_ORDER_KEYS);
  }, [build_graph]);

  // ***************************************************************************
  // * State variables
  // ***************************************************************************
  const [focused_person, _set_focused_person] = useState(null);
  const [focused_trip, _set_focused_trip] = useState(null);
  const [selected_person, _set_selected_person] = useState(null);
  const [selected_trip, _set_selected_trip] = useState(null);
  const [[person_indices_filter, trip_indices_filter], _set_filters] = useState([[...Array(default_people_sorting.length).keys()], [...Array(trip_dates.length).keys()]]);
  // The orderings to sort the displayed people by, highest priority first. The
  // order itself is not held in state: it is recomputed from these and from
  // whatever the filter currently leaves in.
  const [people_order_keys, _set_people_order_keys] = useState(DEFAULT_ORDER_KEYS);
  const [nora_mode, _set_nora_mode] = useState(false);
  // Whether the filter menu's controls are away from their defaults. MenuFilter
  // owns the full filter state and only passes this boolean up to the top level.
  const [filter_active, set_filter_active] = useState(false);

  // Use a ref for tracking whether mouseover events are enabled or disabled so
  // that it can be updated and read from synchronously
  const animating = useRef(false);

  // Suppresses hover-driven focus changes until the cursor actually moves again.
  // Needed because browsers can fire a spurious mouseenter on whatever element ends
  // up under a stationary cursor after an overlay covering it is removed (e.g. after
  // picking a search result), even though the user never moved their mouse there.
  const suppress_hover_until_move = useRef(false);

  // ***************************************************************************
  // * Compute the person, trip and person_trip data in three passes: build the
  // * graph, work out the order to draw it in, then lay out all the angles.
  // ***************************************************************************
  const [segment_gap, people, trips] = useMemo(() => {
    // *************************************************************************
    // Pass 1: the graph itself, with the filter applied. The filter is expressed
    // in person_indices, so turn it back into the columns build_graph works in.
    // *************************************************************************
    const columns_in_filter = new Set(person_indices_filter.map((i) => default_people_sorting[i]));
    const trips_in_filter = new Set(trip_indices_filter);
    const [people_by_column, trips] = build_graph((column) => columns_in_filter.has(column),
                                                  (trip_index) => trips_in_filter.has(trip_index));

    // *************************************************************************
    // Pass 2: hand out person_index off the fixed default ordering, then work
    // out the order to draw people in from the graph as the filter left it.
    // *************************************************************************
    let people = {};
    for (let person_index = 0; person_index < default_people_sorting.length; person_index++) {
      const person = people_by_column[default_people_sorting[person_index]];
      person.person_index = person_index;
      for (let pt in person.person_trips) {
        person.person_trips[pt].person_index = person_index;
      }
      people[person_index] = person;
    }
    const display_order = order_people(default_people_sorting, people_by_column, people_order_keys);
    // Sort the person_trips for each trip into the order the people are drawn
    // in, since that is the order the menu lists a trip's voyagers in.
    let display_position = {};
    display_order.forEach((column, position) => {
      display_position[people_by_column[column].person_index] = position;
    });
    for (let trip_index in trips) {
      trips[trip_index].person_trips.sort((a, b) => display_position[a.person_index] - display_position[b.person_index]);
    }

    // *************************************************************************
    // Pass 3: the angles. Everyone still in the filter gets a slice of the
    // circle in proportion to how many of their trips are in it, with a gap
    // between people, and the selected person's slice zoomed.
    // *************************************************************************
    const SELECTED_PERSON_ZOOM_FACTOR = 2;
    // Compute num_person_trips and num_nontrivial_people, for determing segment angle.
    let num_person_trips = 0;
    let num_nontrivial_people = 0;
    for (let i = 0; i < display_order.length; i++) {
      const person = people_by_column[display_order[i]];
      // Skip this person if not in filter
      if (!person.in_filter) continue;
      const trips_for_this_person = person.person_trips.filter((pt) => pt.in_filter).length;
      const zoom_factor = person.person_index === selected_person ? SELECTED_PERSON_ZOOM_FACTOR : 1;
      num_person_trips += trips_for_this_person * zoom_factor;
      num_nontrivial_people += (trips_for_this_person !== 0);
    }

    const PERSON_TRIP_TO_GAP_RATIO = 2;
    const num_segments = PERSON_TRIP_TO_GAP_RATIO * num_person_trips + num_nontrivial_people;
    const angle_per_segment = 2 * Math.PI / num_segments;
    const angle_per_person_trip = PERSON_TRIP_TO_GAP_RATIO * angle_per_segment;

    // Start just past 12 o'clock.
    let angle_cumulutive = angle_per_segment / 2;
    for (let i = 0; i < display_order.length; i++) {
      // i is the position of the person around the circle.
      const person = people_by_column[display_order[i]];
      const zoom_factor = (person.person_index === selected_person) ? SELECTED_PERSON_ZOOM_FACTOR : 1;
      person.start_angle = person.in_filter ? angle_cumulutive : null;
      for (let pt in person.person_trips) {
        const person_trip = person.person_trips[pt];
        // Anyone the filter left out keeps the null angles they were built with.
        if (!person_trip.in_filter) continue;
        person_trip.start_angle = angle_cumulutive;
        angle_cumulutive += angle_per_person_trip * zoom_factor;
        person_trip.end_angle = angle_cumulutive;
      }
      person.end_angle = angle_cumulutive;

      // Insert gap before next person
      if (person.in_filter) {
        angle_cumulutive += angle_per_segment;
      }
    }

    return [angle_per_segment, people, trips]
  }, [build_graph, default_people_sorting, people_order_keys, selected_person, person_indices_filter, trip_indices_filter]);

  // ***************************************************************************
  // * Custom state updaters
  // ***************************************************************************
  const set_focused_person = useCallback((person_index) => {
    if (!animating.current && !suppress_hover_until_move.current) {
      _set_focused_person(person_index);
      _set_focused_trip(null);
    }
  }, []);

  const set_focused_trip = useCallback((trip_index) => {
    if (!animating.current && !suppress_hover_until_move.current) {
      _set_focused_person(null);
      _set_focused_trip(trip_index);
    }
  }, []);

  const set_selected_person = useCallback((person_index) => {
    _set_selected_person(person_index);
    _set_selected_trip(null);
    // Set focused person/trip too
    _set_focused_person(person_index);
    _set_focused_trip(null);
  }, []);

  // The view is Nora's, so entering it selects her. Deliberately not via
  // set_selected_person: that also sets the focus, and focus means "the pointer
  // is on this", which would dim the whole graph the moment you arrive.
  const set_nora_mode = useCallback((new_nora_mode) => {
    _set_nora_mode(new_nora_mode);
    if (new_nora_mode) {
      const nora = Object.values(people).find(person => person.name === NORA_NAME);
      if (nora) {
        _set_selected_person(nora.person_index);
        _set_selected_trip(null);
        _set_focused_person(null);
        _set_focused_trip(null);
      }
    }
  }, [people]);

  // Nora Mode is best entered with no filters so double check before
  // entering if filters are set.
  const request_nora_mode = useCallback((new_nora_mode) => {
    if (new_nora_mode && filter_active &&
        !window.confirm("Nora Mode is recommended with no filters set - are you sure you wish to continue?")) {
      return;
    }
    set_nora_mode(new_nora_mode);
  }, [filter_active, set_nora_mode]);

  const set_selected_trip = useCallback((trip_index) => {
    _set_selected_trip(trip_index);
    _set_focused_trip(trip_index);
    // Only deselect person if they weren't on this trip
    let selected_person_on_this_trip = false;
    const trip = trips[trip_index];
    for (let i in trip.person_trips) {
      if (trip.person_trips[i].person_index === selected_person) {
        selected_person_on_this_trip = true;
      }
    }
    _set_focused_person(null);
    if (!selected_person_on_this_trip) {
      _set_selected_person(null);
    }
  }, [selected_person, trips]);

  const clear_selection = useCallback(() => {
    _set_selected_person(null);
    _set_selected_trip(null);
    _set_focused_trip(null);
    _set_focused_person(null);
  }, []);

  const set_filter = useCallback((new_person_indices_filter, new_trip_indices_filter) => {
    // If the selected person/trip is no longer in the filter, deselect it
    if (selected_person !== null && !new_person_indices_filter.includes(selected_person)) {
      _set_selected_person(null);
    }
    if (selected_trip !== null && !new_trip_indices_filter.includes(selected_trip)) {
      _set_selected_trip(null);
    }
    // Same for highlighted
    if (focused_person !== null && !new_person_indices_filter.includes(focused_person)) {
      _set_focused_person(null);
    }
    if (focused_trip !== null && !new_trip_indices_filter.includes(focused_trip)) {
      _set_focused_trip(null);
    }
    _set_filters([new_person_indices_filter, new_trip_indices_filter]);
  }, [selected_person, selected_trip, focused_person, focused_trip]);

  // Clicking an ordering promotes it to top priority and pushes the others down
  // a place, so the ordering that was in charge before the click becomes the tie
  // break, and the one before that the tie break after that.
  const update_people_order = useCallback((order_key) => {
    _set_people_order_keys((order_keys) => [order_key, ...order_keys.filter((key) => key !== order_key)]);
  }, []);

  const suppress_hover_after_menu_select = useCallback(() => {
    suppress_hover_until_move.current = true;
  }, []);

  const on_pointer_activity = useCallback(() => {
    suppress_hover_until_move.current = false;
  }, []);

  // ***************************************************************************
  // * Height
  // ***************************************************************************
  // Pinned rather than left to 100dvh, which follows the on-screen keyboard now
  // that the keyboard shrinks the layout viewport - see interactive-widget in
  // index.html. The container centres its contents, so following it would ride
  // the whole diagram up while a menu field is focused and drop it again on
  // close. useWindowDimensions ignores the keyboard, so this is the height the
  // app had before it opened.
  const { height: app_height } = useWindowDimensions();

  // ***************************************************************************
  // * Highlighted person/trip
  // ***************************************************************************
  let highlighted_person = null;
  let highlighted_trip = null;
  if (focused_person !== null || focused_trip !== null) {
    highlighted_person = focused_person;
    highlighted_trip = focused_trip;
  } else {
    // It's possible to have both a person and a trip selected at the same time,
    // so be a bit careful with highlighting here. selected_trip takes precedence.
    if (selected_trip !== null) {
      highlighted_trip = selected_trip;
    } else {
      highlighted_person = selected_person;
    }
  }

  return (
    <div className="full_screen_app_container"
         style={{ height: app_height }}
         onClick={clear_selection}
         onMouseMove={on_pointer_activity}
         onTouchStart={on_pointer_activity}>
      {nora_mode ?
        <NoraMode people={people}
                  trips={trips}
                  focused_person={focused_person}
                  selected_person={selected_person}
                  selected_trip={selected_trip}
                  set_focused_person={set_focused_person}
                  set_selected_person={set_selected_person}/> :
        <ChordDiagram people={people}
                      trips={trips}
                      segment_gap={segment_gap}
                      num_trips={data_binary.length}
                      highlighted_person={highlighted_person}
                      highlighted_trip={highlighted_trip}
                      focused_person={focused_person}
                      focused_trip={focused_trip}
                      selected_person={selected_person}
                      selected_trip={selected_trip}
                      set_focused_person={set_focused_person}
                      set_focused_trip={set_focused_trip}
                      set_selected_person={set_selected_person}
                      set_selected_trip={set_selected_trip}
                      order_options={ORDER_OPTIONS}
                      people_order_descr={people_order_keys[0]}
                      update_people_order={update_people_order}
                      animating={animating}
                      hammock_diary_urls={hammock_diary_urls}/>}
      <Menu people={people} trips={trips}
            selected_person={selected_person} selected_trip={selected_trip}
            set_selected_person={set_selected_person} set_selected_trip={set_selected_trip}
            suppress_hover_after_menu_select={suppress_hover_after_menu_select}
            person_indices_filter={person_indices_filter} trip_indices_filter={trip_indices_filter} set_filter={set_filter}
            set_filter_active={set_filter_active}
            nora_mode={nora_mode} set_nora_mode={request_nora_mode} />
    </div>
  );
};

export default VoyageModel;
